import { createContext, useContext, useEffect, useRef } from 'react';

export type KeyboardScopePriority = 'overlay' | 'search-dropdown' | 'table-cell' | 'page-action' | 'global';

export type KeyboardBindings = Record<string, (event: KeyboardEvent) => void>;

export interface RegisteredScope {
  id: string;
  priority: KeyboardScopePriority;
  allowInEditable: boolean;
  bindingsRef: { current: KeyboardBindings };
}

export interface KeyboardScopeApi {
  register: (scope: RegisteredScope) => void;
  unregister: (id: string) => void;
}

export const KeyboardScopeContext = createContext<KeyboardScopeApi | null>(null);

interface UseRegisterShortcutsOptions {
  active?: boolean;
  priority: KeyboardScopePriority;
  allowInEditable?: boolean;
}

export const useRegisterShortcuts = (
  id: string,
  bindings: KeyboardBindings,
  { active = true, priority, allowInEditable = false }: UseRegisterShortcutsOptions,
) => {
  const api = useContext(KeyboardScopeContext);
  const bindingsRef = useRef<KeyboardBindings>(bindings);
  bindingsRef.current = bindings;

  useEffect(() => {
    if (!api || !active) return undefined;
    api.register({ id, priority, allowInEditable, bindingsRef });
    return () => api.unregister(id);
  }, [api, active, id, priority, allowInEditable]);
};
